import type { ChangeEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { headerNavigationSlice } from "../../../common/Header/redux/slice";
import type { ServiceLinksType } from "../../../common/Header/types";
import { recreateAllApis } from "../../../services/api/config/apiConfig";
import type { TApiName } from "../../../services/api/config/types";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { navigateToRoute } from "../../../shared/utils/navigate-to-route";
import { authAction } from "../redux/actions";
import {
  selectIsUserInfoLoading,
  selectUserSettingsObject,
  selectValidationData,
} from "../redux/selectors";
import { setUserInfo, setValidation } from "../redux/slice";
import type { AuthType } from "../redux/types";
import { LAST_SELECTED_KEY } from "../../../app/state/storageObjectSlice/constants";

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const settingsObject = useAppSelector(selectUserSettingsObject);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const isLoading = useAppSelector(selectIsUserInfoLoading);
  const validationData = useAppSelector(selectValidationData);
  const navigate = useNavigate();

  const handleChangeUserName = (event: ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const onSubmit = (authType: AuthType) => {
    if (!username || !password) {
      dispatch(setValidation({ username, password }));
      return;
    }

    dispatch(
      authAction({
        credentials: { username, password },
        authType,
      }),
    )
      .unwrap()
      .then((res) => {
        if (!res) return;
        const { userInfo, userSettingsObject } = res;
        const links = userSettingsObject?.value?.user_settings?.app_links;
        localStorage.removeItem(LAST_SELECTED_KEY);
        if (authType === "app") {
          dispatch(headerNavigationSlice.actions.initializeVisibleLinks(links));
          window.location.reload();
        } else {
          dispatch(headerNavigationSlice.actions.initializeVisibleLinks(links));
          const baseUrlOverrides: Partial<Record<TApiName, string>> = {};
          if (userInfo.thread_url)
            baseUrlOverrides.thread = userInfo.thread_url;
          if (userInfo.thread_model_url)
            baseUrlOverrides.model = userInfo.thread_model_url;
          if (userInfo.tg_bot_service_url)
            baseUrlOverrides.bot = userInfo.tg_bot_service_url;
          recreateAllApis(
            () => dispatch(setUserInfo(null)),
            !!userInfo.replacement_token,
            baseUrlOverrides,
          );
          if (
            userInfo["user-groups"].includes("/assitant") &&
            links.includes("/assitant")
          ) {
            navigate("/assitant");
          } else {
            navigateToRoute(links, navigate);
          }
        }
      })
      .catch((error) => {
        dispatch(setValidation({ credentials: true }));
      })
      .finally(() => {
        const links = settingsObject?.value?.user_settings?.app_links;
        dispatch(
          headerNavigationSlice.actions.initializeVisibleLinks(
            links as ServiceLinksType,
          ),
        );
      });
  };

  return {
    username,
    password,
    isLoading,
    validationData,
    onSubmit,
    handleChangePassword,
    handleChangeUserName,
  };
};
